import React, { ReactNode } from 'react';
import { TextFieldProps } from '@material-ui/core';
import clsx from 'clsx';
import TextInput from 'product_modules/components/TextInput';
import { CalendarImage } from 'product_modules/static/images';
import { LoaderState } from 'product_modules/components/LoaderWithState/LoaderWithState';
import styles from './Input.module.scss';

export type InputSize = 'form' | 'default' | 'small';

interface DatePickerInputProps {
  onIconClick: (e: React.MouseEvent<Element, MouseEvent>) => void;
  showLoader?: boolean;
  loaderState?: LoaderState | null;
  onLoaderStateReset?: () => void;
  inputClassName?: string;
  inputSize?: InputSize;
  inputIcon?: ReactNode;
  errorMessage?: string;
  readOnly?: boolean;
  tooltip?: string;
  useSimplifiedInput?: boolean;
  style?: React.CSSProperties;
  titleHint?: string;
}

const DatePickerInput = ({
  value,
  label,
  placeholder,
  onChange,
  onFocus,
  onBlur,
  onIconClick,
  readOnly,
  disabled,
  showLoader,
  loaderState,
  onLoaderStateReset,
  inputRef,
  tabIndex,
  inputClassName,
  required,
  errorMessage,
  inputSize = 'default',
  inputIcon,
  tooltip,
  useSimplifiedInput,
  style,
  titleHint,
}: DatePickerInputProps & TextFieldProps) => {
  const renderOverlay = () => {
    if (inputIcon) {
      return null;
    }

    return (
      <div className={styles.calendarInputOverlay} onClick={onIconClick}>
        <p
          className={clsx(
            styles.calendarIconLeftOffset,
            useSimplifiedInput && styles.calendarIconLeftOffsetSimplified,
            styles[inputSize],
          )}
        >
          {value ? (value as ReactNode) : placeholder}
        </p>
        <div
          className={clsx(
            styles.calendarIconContainer,
            useSimplifiedInput && styles.calendarIconContainerSimplified,
          )}
        >
          <CalendarImage
            className={clsx(
              styles.calendarIcon,
              useSimplifiedInput && styles.calendarIconSimplified,
              disabled && styles.disabledCalendarIcon,
            )}
          />
        </div>
      </div>
    );
  };

  return (
    <div
      ref={inputRef}
      className={clsx(styles.container, useSimplifiedInput && styles.containerSimplified)}
    >
      <TextInput
        value={value as string}
        labelTitle={label as string}
        placeholder={placeholder}
        onChange={onChange}
        onFocus={onFocus}
        onBlur={onBlur}
        hasRightNeighbour
        hasRightPadding
        readOnly={readOnly}
        disabled={disabled}
        showLoader={showLoader}
        loaderState={loaderState}
        onLoaderStateReset={onLoaderStateReset}
        required={required}
        loaderClassName={styles.loader}
        tabIndex={tabIndex}
        errorMessage={errorMessage}
        className={clsx(
          styles[inputSize],
          inputClassName,
          useSimplifiedInput && styles.inputSimplified,
        )}
        inputIcon={inputIcon}
        renderOverlay={renderOverlay}
        tooltip={tooltip}
        useSimplifiedInput={useSimplifiedInput}
        style={style}
        titleHint={titleHint}
        type="tel"
      />
    </div>
  );
};

export default DatePickerInput;
